/**
 * VÉRAULT — Essentials · Trip Budget (smart, two-way).
 * Four summary cards + four collapsed category accordions (Flights / Hotels /
 * Activities / Ground Transportation), then a collapsed "Your Spending & All-In
 * Total" pool for client-entered expenses (Serenity Spa, added activities,
 * additional hotel charges). Client entries never touch the core $10,000
 * remaining — only the advisor can reclassify them.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Plus, Pencil, Trash2, Lock, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../../index";
import { usePreviewBase } from "../../../../contexts/PreviewContext";

const money = (n, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(n || 0));
const moneyRound = (n, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(Number(n || 0));

function OwnershipChip({ label }) {
  const client = label !== "Booked by VÉRAULT";
  return (
    <span className={`font-mono text-[8.5px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full ${
      client ? "bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))]" : "bg-[rgba(0,0,0,0.05)] text-[hsl(var(--foreground-muted))]"
    }`}>{label}</span>
  );
}

function Accordion({ title, right, testid, defaultOpen = false, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="rounded-lg border border-[rgba(0,0,0,0.08)] bg-white/50 overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--accent-gold))]/[0.04] transition-colors"
        data-testid={`${testid}-toggle`}>
        <span className="flex items-center gap-2">
          <ChevronDown className={`size-4 text-[hsl(var(--foreground-muted))] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-secondary))]">{title}</span>
        </span>
        <span className="font-serif text-[15px] text-[hsl(var(--foreground))]">{right}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.26 }} className="overflow-hidden">
            <div className="px-4 pb-4 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function LineItem({ it, cur }) {
  return (
    <div className="py-2.5 border-t border-[rgba(0,0,0,0.05)] first:border-t-0 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-serif text-[14px] leading-snug">{it.label}</p>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          {it.dates && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[hsl(var(--foreground-muted))]">{it.dates}</span>}
          <OwnershipChip label={it.ownership} />
          <span className={`font-mono text-[9px] uppercase tracking-[0.12em] ${it.paid ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}>{it.status_label}</span>
        </div>
        {it.detail && <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1">{it.detail}</p>}
      </div>
      <span className="font-serif text-[14px] whitespace-nowrap shrink-0">{money(it.amount, cur)}</span>
    </div>
  );
}

const EMPTY_FORM = { label: "", date: "", location: "", provider: "", participants: "", amount: "", booking_status: "", payment_status: "", confirmation: "", notes: "" };

function EntryForm({ initial, type, onSave, onCancel }) {
  const [f, setF] = React.useState({ ...EMPTY_FORM, ...(initial || {}) });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const isHotel = type === "hotel_charge";
  return (
    <div className="rounded-md border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/[0.04] p-3 space-y-2" data-testid="budget-entry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={f.label} onChange={set("label")} placeholder={isHotel ? "Description (e.g. Parking)" : "Activity name"} data-testid="entry-field-label" className="col-span-2 rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
        <input value={f.amount} onChange={set("amount")} type="number" step="0.01" placeholder="$0.00" data-testid="entry-field-amount" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
        <input value={f.date} onChange={set("date")} type="date" data-testid="entry-field-date" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-mono text-[12px] text-[hsl(var(--foreground-muted))]" />
        {isHotel ? (
          <select value={f.category || ""} onChange={set("category")} data-testid="entry-field-category" className="col-span-2 rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]">
            <option value="">Category…</option>
            {["Parking", "Dining", "Resort fee", "Minibar", "Incidentals", "Upgrade", "Other"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <>
            <input value={f.provider} onChange={set("provider")} placeholder="Provider" data-testid="entry-field-provider" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
            <input value={f.participants} onChange={set("participants")} placeholder="Participants" data-testid="entry-field-participants" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
            <input value={f.booking_status} onChange={set("booking_status")} placeholder="Booking status" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
            <input value={f.payment_status} onChange={set("payment_status")} placeholder="Payment status" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
            <input value={f.confirmation} onChange={set("confirmation")} placeholder="Confirmation #" className="col-span-2 rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
          </>
        )}
        <textarea value={f.notes} onChange={set("notes")} placeholder="Notes" rows={2} className="col-span-2 rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
      </div>
      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} data-testid="entry-cancel" className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] px-2.5 py-1.5">Cancel</button>
        <button type="button" onClick={() => onSave(f)} data-testid="entry-save" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--accent-gold))] text-white px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em]"><Check className="size-3" strokeWidth={2} /> Save</button>
      </div>
    </div>
  );
}

export default function BudgetShelf({ budget, tripId, section = "main" }) {
  const { isPreview } = usePreviewBase();
  const [adding, setAdding] = React.useState(null); // 'activity' | 'hotel_charge'
  const [editingId, setEditingId] = React.useState(null);

  if (!budget || !budget.summary) return null;
  const cur = budget.currency || "USD";
  const s = budget.summary;
  const ys = budget.your_spending || { total: 0, entries: [] };
  const cats = budget.categories || [];

  const refresh = () => window.dispatchEvent(new CustomEvent("vt:sync-changed:essentials"));

  const saveEntry = async (type, form, id) => {
    if (isPreview) return;
    const payload = { type, ...form, amount: parseFloat(form.amount || 0) || 0 };
    try {
      if (id && id !== "new") await api.patch(`/client-portal/trips/${tripId}/budget/entries/${id}`, payload);
      else await api.post(`/client-portal/trips/${tripId}/budget/entries`, payload);
      setAdding(null); setEditingId(null);
      toast.success("Saved to Your Spending");
      refresh();
    } catch (e) {
      toast.error(e?.response?.status === 423 ? "Your advisor locked this item" : "Couldn't save — please try again");
    }
  };

  const deleteEntry = async (id) => {
    if (isPreview) return;
    try {
      await api.delete(`/client-portal/trips/${tripId}/budget/entries/${id}`);
      toast.success("Removed"); refresh();
    } catch (e) {
      toast.error(e?.response?.status === 423 ? "Your advisor locked this item" : "Couldn't remove");
    }
  };

  const CARDS = [
    ["Paid", s.paid], ["Booked · Not Yet Paid", s.unpaid_committed],
    ["Total Committed", s.committed], [`Remaining of ${moneyRound(s.original, cur)}`, s.remaining],
  ];

  // ---------- MAIN: four cards + four category accordions ----------
  if (section === "main") {
    return (
      <section className="space-y-4" data-testid="essentials-budget-shelf">
        <div className="flex items-center gap-2">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">Trip Budget</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {CARDS.map(([label, val], i) => (
            <div key={i} className={`rounded-lg border px-3.5 py-3 ${i === 3 ? "border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--accent-gold))]/[0.05]" : "border-[rgba(0,0,0,0.08)] bg-white/50"}`} data-testid={`budget-card-${i}`}>
              <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] leading-tight">{label}</p>
              <p className="font-serif text-[19px] mt-1">{money(val, cur)}</p>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {cats.map((c) => (
            <Accordion key={c.key} title={c.label} right={money(c.subtotal, cur)} testid={`budget-cat-${c.key}`}>
              {c.items.length === 0 ? (
                <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] py-2">Nothing booked in this category yet.</p>
              ) : c.items.map((it) => <LineItem key={it.id} it={it} cur={cur} />)}
              {c.key === "hotels" && (
                <div className="mt-3 pt-3 border-t border-[rgba(0,0,0,0.06)] space-y-1.5">
                  <Row label="Original (VÉRAULT-booked)" val={money(c.original_amount, cur)} />
                  <Row label="Additional charges (you)" val={money(c.additional_amount, cur)} />
                  <Row label="Combined hotel total" val={money(c.combined_amount, cur)} strong />
                  {!isPreview && (adding === "hotel_charge" ? (
                    <EntryForm type="hotel_charge" onSave={(f) => saveEntry("hotel_charge", f)} onCancel={() => setAdding(null)} />
                  ) : (
                    <button type="button" onClick={() => setAdding("hotel_charge")} data-testid="add-hotel-charge" className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))] hover:underline"><Plus className="size-3" strokeWidth={2} /> Add additional charge</button>
                  ))}
                </div>
              )}
            </Accordion>
          ))}
        </div>
      </section>
    );
  }

  // ---------- SPENDING: entries + add controls (totals live atop Activities) ----------
  return (
    <section className="space-y-4" data-testid="essentials-your-spending-shelf">
      <Accordion title="Your Spending — details" right={money(ys.total, cur)} testid="budget-your-spending" defaultOpen>
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mb-2">Extra to your core budget — these don't change your Remaining.</p>

        {ys.entries.length === 0 && (
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] py-2">Nothing yet. Mark an activity purchased or set a Serenity Spa amount above, or add your own below.</p>
        )}
        <div className="space-y-2">
          {ys.entries.map((e) => (
            editingId === e.id ? (
              <EntryForm key={e.id} type={e.type} initial={e} onSave={(f) => saveEntry(e.type, f, e.id)} onCancel={() => setEditingId(null)} />
            ) : (
              <div key={e.id} className="rounded-md border border-[rgba(0,0,0,0.08)] bg-white/50 p-3 flex items-start justify-between gap-3" data-testid={`your-spending-entry-${e.id}`}>
                <div className="min-w-0">
                  <p className="font-serif text-[14px]">{e.label}</p>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    {e.date && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[hsl(var(--foreground-muted))]">{e.date}</span>}
                    <OwnershipChip label={e.ownership} />
                    {e.category && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[hsl(var(--foreground-muted))]">{e.category}</span>}
                    {e.locked && <Lock className="size-3 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />}
                  </div>
                  {e.notes && <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1">{e.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-serif text-[14px]">{money(e.amount, cur)}</span>
                  {!isPreview && e.editable && (
                    <>
                      <button type="button" onClick={() => setEditingId(e.id)} data-testid={`entry-edit-${e.id}`} className="size-7 grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"><Pencil className="size-3.5" strokeWidth={1.5} /></button>
                      <button type="button" onClick={() => deleteEntry(e.id)} data-testid={`entry-delete-${e.id}`} className="size-7 grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-red-600"><Trash2 className="size-3.5" strokeWidth={1.5} /></button>
                    </>
                  )}
                </div>
              </div>
            )
          ))}
        </div>

        {!isPreview && (adding === "activity" ? (
          <div className="mt-2"><EntryForm type="activity" onSave={(f) => saveEntry("activity", f)} onCancel={() => setAdding(null)} /></div>
        ) : (
          <button type="button" onClick={() => setAdding("activity")} data-testid="add-activity" className="mt-3 inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))] hover:underline"><Plus className="size-3" strokeWidth={2} /> Add a new activity</button>
        ))}
      </Accordion>
    </section>
  );
}

function Row({ label, val, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`font-mono text-[9px] uppercase tracking-[0.14em] ${strong ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"}`}>{label}</span>
      <span className={`font-serif ${strong ? "text-[15px]" : "text-[13px] text-[hsl(var(--foreground-secondary))]"}`}>{val}</span>
    </div>
  );
}
